import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Sparkles, Loader2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import useOnboardingSteps from '../hooks/useOnboardingSteps';
import useJobSyncStatus from '../hooks/useJobSyncStatus';
import useOnboardingActivation from '../hooks/useOnboardingActivation';
import { saveOnboardingPrefs } from '../utils/onboardingStatus';
import OnboardingUpworkStep from './OnboardingUpworkStep';
import OnboardingActivationPanel from './OnboardingActivationPanel';

const AUTOMATED_STEP_IDS = new Set(['job_filters', 'sync_jobs', 'ai_knowledge', 'first_proposal']);
const ACTIVATION_STAGE_BY_STEP = {
  ai_knowledge: 'profile_sync',
  job_filters: 'job_filters',
  sync_jobs: 'job_sync',
  first_proposal: 'first_proposal',
};

export function OnboardingFlowPage() {
  const navigate = useNavigate();
  const {
    loading,
    steps,
    isEligible,
    completedCount,
    allCompleted,
    onboardingPrefs,
    user,
    tenant,
    refreshSteps,
    focusedStepId,
    setFocusedStepId,
  } = useOnboardingSteps({
    autoRefreshMs: 15000,
    refreshOnFocus: true,
  });
  const [expandedStep, setExpandedStep] = useState(null);
  const {
    activation,
    loading: activationLoading,
    starting: activationStarting,
    error: activationError,
    start: startActivation,
  } = useOnboardingActivation({ enabled: isEligible });

  useEffect(() => {
    if (focusedStepId === 'connect_upwork') {
      setExpandedStep('connect_upwork');
    }
  }, [focusedStepId]);

  useEffect(() => {
    if (!focusedStepId) return;
    const focusedStep = steps.find((step) => step.id === focusedStepId);
    if (focusedStep?.completed) {
      setFocusedStepId(null);
    }
  }, [steps, focusedStepId, setFocusedStepId]);

  useJobSyncStatus({
    enabled: isEligible,
    onSyncCompleted: () => {
      refreshSteps({ showLoading: false, forceRefresh: true });
    },
  });
  const connectStep = useMemo(
    () => steps.find((step) => step.id === 'connect_upwork') || null,
    [steps]
  );
  const accessEnabled = Boolean(connectStep?.completed);
  const activationDoneCount = activation?.progress?.doneCount || 0;

  useEffect(() => {
    if (!activationDoneCount) return;
    refreshSteps({ showLoading: false, forceRefresh: true });
  }, [activationDoneCount, refreshSteps]);

  useEffect(() => {
    if (!isEligible || activationLoading || activationStarting) return;
    if (!accessEnabled || !activation?.canAutoActivate) return;
    if (allCompleted) return;
    if (activation.startedAt || activation.state !== 'idle') return;

    startActivation({ source: 'oauth', trigger: 'onboarding_page' }).catch(() => {
      // Surface comes from activationError; no extra action needed here
    });
  }, [
    accessEnabled,
    activation?.canAutoActivate,
    activation?.startedAt,
    activation?.state,
    activationLoading,
    activationStarting,
    allCompleted,
    isEligible,
    startActivation,
  ]);

  const handleSkipForNow = () => {
    const skippedUntil = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
    saveOnboardingPrefs(user?._id, tenant?._id, {
      ...onboardingPrefs,
      skipped: true,
      skippedUntil,
    });
    setFocusedStepId(null);
    navigate('/dashboard');
  };

  const handleCompleteOnboarding = () => {
    saveOnboardingPrefs(user?._id, tenant?._id, {
      ...onboardingPrefs,
      completed: true,
      skipped: false,
      skippedUntil: null,
    });
    setFocusedStepId(null);
    navigate('/dashboard');
  };

  if (!loading && !isEligible) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary-50 via-white to-sky-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading onboarding...</p>
        </div>
      </div>
    );
  }

  const canContinue = allCompleted
    || steps.length === 0
    || (accessEnabled && (!activation?.canAutoActivate || Boolean(activation?.startedAt) || activation?.state === 'running'));

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 via-white to-sky-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <Sparkles className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Propel</h1>
          <p className="text-lg text-gray-600">Let&apos;s get your workspace set up in just a few steps</p>
        </div>

        {connectStep && !connectStep.completed && (
          <div className="mb-6">
            <div className={`bg-white rounded-xl shadow-md p-6 transition-all ${
              focusedStepId === 'connect_upwork'
                ? 'border-2 border-primary-400 ring-1 ring-primary-200'
                : 'border-2 border-transparent'
            }`}>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{connectStep.title}</h3>
              <p className="text-gray-600 mb-3">{connectStep.description}</p>
              <button
                type="button"
                onClick={() => {
                  setFocusedStepId('connect_upwork');
                  setExpandedStep(expandedStep === 'connect_upwork' ? null : 'connect_upwork');
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                {expandedStep === 'connect_upwork' ? 'Collapse' : 'Set Up Upwork'}
                {expandedStep === 'connect_upwork' ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {expandedStep === 'connect_upwork' && (
                <div className="mt-4">
                  <OnboardingUpworkStep
                    onComplete={() => refreshSteps({ showLoading: false, forceRefresh: true })}
                    onRefresh={() => refreshSteps({ showLoading: false, forceRefresh: true })}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mb-6">
          <OnboardingActivationPanel
            accessEnabled={accessEnabled}
            activation={activation}
            stepsCount={steps.length}
            completedCount={completedCount}
          />
        </div>

        {activationError && (
          <div className="mb-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {activationError}
          </div>
        )}

        {steps.some((step) => {
          const stage = activation?.stages?.find((s) => s.id === ACTIVATION_STAGE_BY_STEP[step.id]);
          return AUTOMATED_STEP_IDS.has(step.id) && !step.completed && stage?.state === 'failed';
        }) && (
          <div className="space-y-4 mb-8">
            {steps.filter((step) => {
              const stage = activation?.stages?.find((s) => s.id === ACTIVATION_STAGE_BY_STEP[step.id]);
              return AUTOMATED_STEP_IDS.has(step.id) && !step.completed && stage?.state === 'failed';
            }).map((step) => {
              const activationStage = activation?.stages?.find((s) => s.id === ACTIVATION_STAGE_BY_STEP[step.id]);
              return (
                <div key={step.id} className="bg-white rounded-xl shadow-md p-6 border-2 border-rose-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-6 h-6 text-rose-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{step.title}</h3>
                      <div className="mb-3 flex items-start gap-2 text-sm text-rose-600">
                        <AlertCircle className="mt-0.5 w-4 h-4 flex-shrink-0" />
                        <span>{activationStage?.error || activationStage?.detail || 'This step needs your attention'}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFocusedStepId(step.id);
                          navigate(step.path);
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors"
                      >
                        Fix manually
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={handleSkipForNow}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Skip for now
            </button>

            {canContinue && (
              <button
                type="button"
                onClick={handleCompleteOnboarding}
                className="px-6 py-2 bg-gradient-to-r from-primary-600 to-sky-500 text-white rounded-lg hover:from-primary-700 hover:to-sky-600 transition-colors font-medium flex items-center gap-2"
              >
                Continue to Dashboard
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          <p className="mt-4 text-sm text-gray-500 text-center">
            {activation?.state === 'running'
              ? 'Setup continues in the background — feel free to explore'
              : 'You can return to this guide anytime from your dashboard'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default OnboardingFlowPage;
